import { useEffect, useState } from "react";
import "@/App.css";

const RAJ_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_clean-site-redesign/artifacts/bd437b7r_IMG-20260529-WA0004.jpg";

const LOGO =
  "https://customer-assets.emergentagent.com/job_clean-site-redesign/artifacts/na7yycbx_images%20%286%29.png";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1486325212027-8081e485255e?auto=format&fit=crop&w=2200&q=80";
// Urban commercial / development site context — calm, editorial, brand-aligned.

const SPECIALTIES = [
  "Commercial",
  "Development Sites",
  "Industrial",
  "Land Acquisition",
  "Off-Market",
  "Joint Ventures",
  "Investments",
  "Income Assets",
  "Pre-Market Pipelines",
];

const PRINCIPLES = [
  {
    n: "01",
    title: "Off-Market Site Sourcing",
    body: "Direct access to development sites, commercial holdings and industrial assets before they reach the open market.",
  },
  {
    n: "02",
    title: "Developer-Led Advisory",
    body: "Acquisition strategy built around feasibility, yield and exit, not transaction volume. Every site is qualified to your mandate.",
  },
  {
    n: "03",
    title: "Joint Ventures & Capital",
    body: "Structured introductions between landowners, developers and investors, both domestic and offshore, for JV, equity and project funding.",
  },
  {
    n: "04",
    title: "Queensland Market Depth",
    body: "Deep coverage across Brisbane, Gold Coast and South East Queensland, including zoned land, infill sites, industrial estates and CBD-grade assets.",
  },
];

const NETWORK = [
  "Property Referral Partners",
  "Commercial & Residential Agents",
  "Development & Planning Consultants",
  "Town Planners & Architects",
  "Commercial Lending Institutions",
  "Mortgage Brokers",
  "Accountants & Tax Advisors",
  "Investment Funds & Family Offices",
  "Domestic & Offshore Capital Partners",
];

const SERVICES = [
  {
    n: "S/01",
    title: "Acquisition Mandates",
    body: "Brief-led sourcing of development sites, commercial buildings and industrial assets against your specific feasibility, including site cover, GFA, zoning, yield and exit horizon.",
  },
  {
    n: "S/02",
    title: "Off-Market & Pre-Market Access",
    body: "Direct relationships with landowners, agents and family-held holdings. We bring qualified opportunities to clients before they ever touch a campaign.",
  },
  {
    n: "S/03",
    title: "Feasibility & Due Diligence Support",
    body: "Working alongside town planners, architects and QSs we assist with feasibility framing, planning pathways, environmental, traffic and contamination overlays.",
  },
  {
    n: "S/04",
    title: "Joint Ventures & Capital Structuring",
    body: "Landowner-led JVs, equity introductions and project funding, connecting developers with domestic and offshore capital partners under structured terms.",
  },
  {
    n: "S/05",
    title: "Divestment & Asset Repositioning",
    body: "Quiet, off-campaign divestment of income assets and surplus land, preserving price discovery and protecting the vendor's commercial position.",
  },
  {
    n: "S/06",
    title: "Portfolio & Strategic Advisory",
    body: "Ongoing acquisition planning for developer pipelines, family offices and funds, including pipeline modelling, market intelligence and quarterly reviews.",
  },
];

const ASSET_CLASSES = [
  {
    label: "Development Sites",
    detail:
      "Infill, mid-density, urban renewal, master-planned and englobo land, whether DA approved or raw.",
  },
  {
    label: "Commercial",
    detail:
      "Office, mixed-use, retail strata, neighbourhood centres and CBD-grade holdings.",
  },
  {
    label: "Industrial",
    detail:
      "Logistics, manufacturing, large-format showrooms and industrial estates across SEQ corridors.",
  },
  {
    label: "Land & Englobo",
    detail:
      "Strategic land, future urban, infrastructure-adjacent parcels and englobo holdings.",
  },
  {
    label: "Income Assets",
    detail:
      "Tenanted commercial, industrial and mixed-use assets with secured cashflow profiles.",
  },
  {
    label: "Joint Ventures",
    detail:
      "Landowner-led JVs, equity participation and structured project participations.",
  },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 transition-colors duration-500"
      style={{
        background: scrolled ? "rgba(250, 250, 247, 0.94)" : "transparent",
        backdropFilter: scrolled ? "blur(10px)" : "none",
      }}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 h-[84px] flex items-center justify-between">
        <a
          href="#top"
          data-testid="brand-link"
          className="flex items-center leading-none"
          aria-label="Grandeur Property Group, home page"
        >
          <img
            src={LOGO}
            alt="Grandeur Property Group"
            data-testid="brand-logo"
            className="h-14 md:h-16 w-auto object-contain"
            style={{ mixBlendMode: "multiply" }}
          />
        </a>

        <nav
          data-testid="primary-nav"
          className="hidden md:flex items-center gap-9 text-[0.74rem] uppercase tracking-[0.24em] text-[var(--ink-soft)]"
        >
          <a href="#about" data-testid="nav-about" className="link-underline">
            About
          </a>
          <a href="#services" data-testid="nav-services" className="link-underline">
            Services
          </a>
          <a href="#assets" data-testid="nav-assets" className="link-underline">
            Asset Classes
          </a>
          <a href="#network" data-testid="nav-network" className="link-underline">
            Network
          </a>
          <a href="#process" data-testid="nav-process" className="link-underline">
            Process
          </a>
          <a href="#contact" data-testid="nav-contact" className="link-underline">
            Contact
          </a>
        </nav>

        <a
          href="#contact"
          data-testid="header-cta"
          className="hidden md:inline-flex items-center gap-2 text-[0.72rem] uppercase tracking-[0.22em] border-b border-[var(--ink)] pb-[3px]"
        >
          Make an enquiry
        </a>
      </div>
      <div className="hairline opacity-60" />
    </header>
  );
};

const Hero = () => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative min-h-[100svh] flex items-end pb-16 md:pb-24 pt-32 overflow-hidden"
  >
    <div className="absolute inset-0">
      <img
        src={HERO_IMAGE}
        alt="A quiet luxury residence"
        className="w-full h-full object-cover object-center"
        loading="eager"
      />
      <div
        className="absolute inset-0"
        style={{ background: "rgba(250, 250, 247, 0.86)" }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-2/3"
        style={{
          background:
            "linear-gradient(to top, rgba(250,250,247,1) 0%, rgba(250,250,247,0.96) 40%, rgba(250,250,247,0) 100%)",
        }}
      />
      <div
        className="absolute inset-x-0 top-0 h-32"
        style={{
          background:
            "linear-gradient(to bottom, rgba(250,250,247,1), rgba(250,250,247,0))",
        }}
      />
    </div>

    <div className="relative mx-auto max-w-[1400px] w-full px-6 md:px-12">
      <p
        data-testid="hero-eyebrow"
        className="eyebrow fade-up"
      >
        Brisbane · Queensland · Australia
      </p>
      <h1
        data-testid="hero-headline"
        className="font-display fade-up delay-1 mt-6 text-[2.6rem] sm:text-[3.6rem] md:text-[5.2rem] leading-[0.98] max-w-[18ch]"
      >
        Commercial and development real estate, quietly delivered.
      </h1>
      <p
        data-testid="hero-subcopy"
        className="fade-up delay-2 mt-10 max-w-[58ch] text-[0.98rem] md:text-[1.02rem] leading-[1.7] text-[var(--ink-soft)]"
      >
        Grandeur Property Group sources off-market and pre-market sites for
        developers, investors and joint-venture partners across Queensland.
        Our focus spans commercial, industrial, land and income-producing
        assets.
      </p>
      <div className="fade-up delay-3 mt-12 flex flex-wrap gap-4">
        <a href="#approach" data-testid="hero-cta-approach" className="btn-solid">
          What We Source
        </a>
        <a href="#contact" data-testid="hero-cta-contact" className="btn-quiet">
          Speak with Raj
        </a>
      </div>
    </div>
  </section>
);

const Marquee = () => {
  const items = [...SPECIALTIES, ...SPECIALTIES];
  return (
    <section
      data-testid="marquee-section"
      className="border-y border-[var(--rule)] py-8 md:py-10 overflow-hidden bg-[var(--paper-soft)]"
    >
      <div className="relative whitespace-nowrap">
        <div className="marquee-track inline-flex gap-12 md:gap-16 will-change-transform">
          {items.map((s, i) => (
            <span
              key={`${s}-${i}`}
              data-testid={`marquee-item-${i}`}
              className="font-display text-[1.8rem] md:text-[2.4rem] leading-none flex items-center gap-12 md:gap-16"
            >
              {s}
              <span className="text-[var(--brass)] text-[1rem]">·</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

const About = () => (
  <section
    id="about"
    data-testid="about-section"
    className="bg-[var(--paper-soft)] paper-grain"
  >
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40">
      <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-20 md:mb-28">
        <div className="md:col-span-4">
          <p className="eyebrow">About</p>
          <h2
            data-testid="about-headline"
            className="font-display mt-6 text-[2.4rem] md:text-[3.4rem] leading-[1.02]"
          >
            We source, qualify and secure development sites & commercial assets.
          </h2>
        </div>
        <div className="md:col-span-7 md:col-start-6 space-y-6 text-[1rem] md:text-[1.05rem] leading-[1.85] text-[var(--ink-soft)] max-w-[62ch]">
          <p>
            Grandeur Property Group is a boutique commercial buyer's agency
            and development advisory. Owned and operated by Raj Puran, the
            firm represents developers, investors and capital partners
            acquiring sites and assets across every commercial asset class.
          </p>
          <p>
            Our positioning as a buyer-side advisor gives us unique access to
            off-market and pre-market opportunities, built through long-held
            relationships with landowners, commercial and residential agents,
            and a wide network of referral partners across South East
            Queensland.
          </p>
          <p>
            We understand the practical realities of acquisition for
            developers: feasibility-led briefs, planning risk, capital
            constraints and timing. Our negotiation and sourcing strategy is
            built around those realities, and adjusts as the market changes.
          </p>
          <p>
            Every mandate is principal-led. We work selectively, on a small
            number of meaningful engagements at any time, so that each client
            receives genuine attention from acquisition brief through to
            settlement.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-10 md:gap-20 items-start pt-16 md:pt-20 border-t border-[var(--rule)]">
        <div className="md:col-span-5 order-2 md:order-1">
          <div className="relative">
            <img
              src={RAJ_PORTRAIT}
              alt="Raj Puran, Principal of Grandeur Property Group"
              data-testid="raj-portrait"
              className="w-full h-auto object-cover grayscale-[15%] contrast-[1.02]"
              loading="lazy"
            />
            <div className="absolute -bottom-4 left-0 right-0 h-px bg-[var(--ink)]/15" />
            <p className="mt-5 text-[0.72rem] tracking-[0.28em] uppercase text-[var(--muted)]">
              Raj Puran · Principal & Founder
            </p>
          </div>
        </div>

        <div className="md:col-span-7 order-1 md:order-2">
          <p className="eyebrow">The Principal</p>
          <h2
            data-testid="principal-headline"
            className="font-display mt-6 text-[2.2rem] md:text-[3rem] leading-[1.05]"
          >
            A principal you know by name.
          </h2>
          <div className="mt-10 space-y-6 text-[1rem] md:text-[1.04rem] leading-[1.8] text-[var(--ink-soft)] max-w-[60ch]">
            <p>
              Raj founded Grandeur Property Group on a single conviction: that
              the acquisition of a commercial holding, development site or
              income-producing asset deserves principal-led representation and
              a developer's commercial lens.
            </p>
            <p>
              With a background spanning acquisitions, capital markets and
              private advisory, Raj has structured transactions across
              residential development, industrial estates, mixed-use
              redevelopments and income-producing portfolios, for clients
              ranging from boutique builders to offshore institutional
              capital.
            </p>
            <p>
              From the firm's Milton headquarters, Raj personally oversees
              every mandate. Clients deal with the principal directly, not a
              junior or a handoff, for the full length of the engagement.
            </p>
          </div>
          <a
            href="#contact"
            data-testid="about-cta"
            className="inline-flex items-center gap-3 mt-12 text-[0.74rem] uppercase tracking-[0.24em] border-b border-[var(--ink)] pb-[3px]"
          >
            Begin a conversation
            <span aria-hidden>→</span>
          </a>
        </div>
      </div>
    </div>
  </section>
);

const Services = () => (
  <section
    id="services"
    data-testid="services-section"
    className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40"
  >
    <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-16">
      <div className="md:col-span-5">
        <p className="eyebrow">Services</p>
        <h2
          data-testid="services-headline"
          className="font-display mt-6 text-[2.2rem] md:text-[3.2rem] leading-[1.04]"
        >
          A full acquisition and advisory capability.
        </h2>
      </div>
      <div className="md:col-span-6 md:col-start-7">
        <p className="text-[1rem] md:text-[1.05rem] leading-[1.85] text-[var(--ink-soft)] max-w-[58ch]">
          From the first acquisition brief through to settlement, and across
          the full life of a developer pipeline, Grandeur operates as an
          extension of your in-house team.
        </p>
      </div>
    </div>

    <div className="grid md:grid-cols-2 gap-px bg-[var(--rule)] border border-[var(--rule)]">
      {SERVICES.map((s) => (
        <div
          key={s.n}
          data-testid={`service-${s.n}`}
          className="bg-[var(--paper)] p-8 md:p-10"
        >
          <div className="flex items-baseline gap-6">
            <span className="font-display text-[var(--brass)] text-[0.95rem] tracking-[0.18em]">
              {s.n}
            </span>
            <h3 className="font-display text-[1.5rem] md:text-[1.75rem] leading-snug">
              {s.title}
            </h3>
          </div>
          <p className="mt-5 text-[0.96rem] leading-[1.8] text-[var(--ink-soft)] max-w-[58ch]">
            {s.body}
          </p>
        </div>
      ))}
    </div>
  </section>
);

const AssetClasses = () => (
  <section
    id="assets"
    data-testid="assets-section"
    className="text-[var(--paper)]"
    style={{ background: "var(--deep-navy)" }}
  >
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40">
      <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-20">
        <div className="md:col-span-5">
          <p className="text-[0.7rem] tracking-[0.32em] uppercase text-[var(--brass-soft)]">
            Asset classes
          </p>
          <h2
            data-testid="assets-headline"
            className="font-display mt-6 text-[2.2rem] md:text-[3.2rem] leading-[1.04]"
          >
            What we source across the commercial spectrum.
          </h2>
        </div>
        <div className="md:col-span-6 md:col-start-7">
          <p className="text-[1rem] md:text-[1.05rem] leading-[1.85] text-[var(--paper)]/70 max-w-[58ch]">
            We are asset-class agnostic. Every mandate is qualified against
            commercial fundamentals such as yield, residual value, planning
            pathway, tenancy profile and capital cost, not against category.
          </p>
        </div>
      </div>

      <ul className="divide-y divide-white/10 border-t border-white/10">
        {ASSET_CLASSES.map((a, i) => (
          <li
            key={a.label}
            data-testid={`asset-${i}`}
            className="grid grid-cols-12 gap-6 md:gap-10 py-8 md:py-10 group transition-colors duration-500 hover:bg-white/[0.03]"
          >
            <span className="col-span-1 font-display text-[var(--brass-soft)] text-[0.95rem] pt-2">
              0{i + 1}
            </span>
            <h3 className="col-span-11 md:col-span-4 font-display text-[1.6rem] md:text-[2rem] leading-tight">
              {a.label}
            </h3>
            <p className="col-span-12 md:col-span-7 text-[0.98rem] leading-[1.8] text-[var(--paper)]/75 md:pt-2">
              {a.detail}
            </p>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const Network = () => (
  <section
    id="network"
    data-testid="network-section"
    className="bg-[var(--paper-soft)] paper-grain"
  >
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40">
      <div className="grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-5">
          <p className="eyebrow">Our Network</p>
          <h2
            data-testid="network-headline"
            className="font-display mt-6 text-[2.2rem] md:text-[3.2rem] leading-[1.04]"
          >
            A wider network, working behind every mandate.
          </h2>
          <p className="mt-8 max-w-[44ch] text-[1rem] leading-[1.85] text-[var(--ink-soft)]">
            Grandeur works alongside an established network of advisory groups
            and consultants in the development sector, bringing valuable
            access, insight and execution support to every client acquisition.
          </p>
        </div>

        <div className="md:col-span-7">
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
            {NETWORK.map((item, i) => (
              <li
                key={item}
                data-testid={`network-${i}`}
                className="flex items-start gap-5 py-5 border-b border-[var(--rule)]"
              >
                <span className="font-display text-[var(--brass)] text-[0.85rem] pt-1 whitespace-nowrap min-w-[3rem]">
                  — {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display text-[1.2rem] md:text-[1.3rem] leading-snug">
                  {item}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

const Process = () => (
  <section
    id="process"
    data-testid="process-section"
    className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40"
  >
    <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-16">
      <div className="md:col-span-5">
        <p className="eyebrow">Engagement</p>
        <h2
          data-testid="process-headline"
          className="font-display mt-6 text-[2.2rem] md:text-[3.2rem] leading-[1.04]"
        >
          How an engagement runs.
        </h2>
      </div>
      <div className="md:col-span-6 md:col-start-7">
        <p className="text-[1rem] md:text-[1.05rem] leading-[1.85] text-[var(--ink-soft)] max-w-[58ch]">
          A clear, paced process built around the realities of feasibility,
          capital and timing. Every step is principal-led and transparent.
        </p>
      </div>
    </div>

    <ol className="grid md:grid-cols-4 gap-px bg-[var(--rule)] border border-[var(--rule)]">
      {PRINCIPLES.map((p, i) => (
        <li
          key={p.n}
          data-testid={`process-step-${i}`}
          className="bg-[var(--paper)] p-8 md:p-10 flex flex-col"
        >
          <span className="font-display text-[var(--brass)] text-[0.85rem] tracking-[0.18em]">
            STEP {p.n}
          </span>
          <h3 className="font-display text-[1.4rem] md:text-[1.6rem] mt-4 leading-snug">
            {p.title}
          </h3>
          <p className="mt-4 text-[0.94rem] leading-[1.8] text-[var(--ink-soft)] flex-1">
            {p.body}
          </p>
        </li>
      ))}
    </ol>
  </section>
);

const Contact = () => (
  <section
    id="contact"
    data-testid="contact-section"
    className="text-[var(--paper)]"
    style={{ background: "var(--deep-navy)" }}
  >
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-28 md:py-40">
      <div className="grid md:grid-cols-12 gap-10 md:gap-16">
        <div className="md:col-span-7">
          <p className="text-[0.7rem] tracking-[0.32em] uppercase text-[var(--brass-soft)]">
            Private advisory
          </p>
          <h2
            data-testid="contact-headline"
            className="font-display mt-6 text-[2.4rem] md:text-[3.6rem] leading-[1.05] max-w-[20ch]"
          >
            Considering an acquisition, divestment or joint venture?
          </h2>
          <p className="mt-8 max-w-[56ch] text-[1rem] leading-[1.8] text-[var(--paper)]/75">
            Speak directly with Raj. Every mandate is handled personally and
            in the strictest confidence.
          </p>
        </div>

        <div className="md:col-span-5 md:pl-10 md:border-l md:border-white/10">
          <dl className="space-y-8">
            <div>
              <dt className="text-[0.7rem] tracking-[0.28em] uppercase text-[var(--brass-soft)]">
                Principal
              </dt>
              <dd className="font-display text-[1.5rem] mt-2">Raj Puran</dd>
            </div>
            <div>
              <dt className="text-[0.7rem] tracking-[0.28em] uppercase text-[var(--brass-soft)]">
                Telephone
              </dt>
              <dd className="mt-2">
                <a
                  href="tel:+61478831814"
                  data-testid="contact-phone"
                  className="font-display text-[1.5rem] link-underline"
                >
                  +61 478 831 814
                </a>
              </dd>
            </div>
            <div>
              <dt className="text-[0.7rem] tracking-[0.28em] uppercase text-[var(--brass-soft)]">
                Email
              </dt>
              <dd className="mt-2">
                <a
                  href="mailto:raj@grandeurpropertygroup.com"
                  data-testid="contact-email"
                  className="font-display text-[1.4rem] link-underline break-all"
                >
                  raj@grandeurpropertygroup.com
                </a>
              </dd>
            </div>
            <div>
              <dt className="text-[0.7rem] tracking-[0.28em] uppercase text-[var(--brass-soft)]">
                Office
              </dt>
              <dd className="font-display text-[1.25rem] mt-2 leading-snug">
                Milton, Brisbane
                <br />
                Queensland, Australia
              </dd>
            </div>
          </dl>

          <a
            href="mailto:raj@grandeurpropertygroup.com"
            data-testid="contact-cta"
            className="mt-12 inline-flex items-center gap-3 px-7 py-4 bg-[var(--paper)] text-[var(--ink)] text-[0.76rem] tracking-[0.24em] uppercase transition-colors duration-500 hover:bg-[var(--brass)] hover:text-[var(--paper)]"
          >
            Make an enquiry
            <span aria-hidden>→</span>
          </a>
        </div>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="text-[var(--paper)]/70 border-t border-white/10"
    style={{ background: "var(--deep-navy)" }}
  >
    <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
      <div className="flex items-center gap-4">
        <div className="bg-white p-2">
          <img
            src={LOGO}
            alt="Grandeur Property Group"
            data-testid="footer-logo"
            className="h-12 w-auto object-contain"
          />
        </div>
      </div>
      <div className="text-[0.72rem] tracking-[0.2em] uppercase">
        © {new Date().getFullYear()} · Brisbane, Queensland
      </div>
      <div className="text-[0.72rem] tracking-[0.2em] uppercase">
        Commercial · Development · By Appointment
      </div>
    </div>
  </footer>
);

function App() {
  return (
    <div className="App bg-[var(--paper)] text-[var(--ink)]">
      <Header />
      <main>
        <Hero />
        <Marquee />
        <About />
        <Services />
        <AssetClasses />
        <Network />
        <Process />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}

export default App;
